import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Instagram } from "lucide-react";
import { EASE, Kicker, KineticWords, Reveal, SectionHead } from "@/components/Reveal";
import { ArtworkCard } from "@/components/ArtworkTile";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import { BUSINESS, MARQUEE_ITEMS, MISSION_CHAPTERS, SERVICES } from "@/data/site";
import { useSiteData } from "@/context/SiteData";
import { usePageMeta } from "@/lib/meta";

function Hero() {
  const { t } = useSiteData();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const bigX = useTransform(sx, (v) => v * 22);
  const bigY = useTransform(sy, (v) => v * 16);
  const smallX = useTransform(sx, (v) => v * -34);
  const smallY = useTransform(sy, (v) => v * -24);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const artY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="divine-rays relative overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/2 top-[-8rem] h-96 w-[52rem] -translate-x-1/2 rounded-full bg-[#D4AF37]/15 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-4 pb-24 pt-16 sm:px-6 md:pt-24 lg:grid-cols-12 lg:items-center lg:pb-32">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <Kicker>An Artistic Ministry by Natalia</Kicker>
          </motion.div>

          <h1 className="mt-7 font-serif text-4xl font-light leading-[1.06] tracking-tight text-[#1C1815] sm:text-5xl lg:text-6xl">
            <KineticWords
              lines={[
                <>{t("hero_line1", "Faith.")}</>,
                <>{t("hero_line2", "Art.")}</>,
                <span className="italic text-[#9A7A3F]">{t("hero_line3", "Divine Inspiration.")}</span>,
              ]}
            />
          </h1>

          <motion.p
            className="mt-7 max-w-xl text-base font-light leading-relaxed text-[#4A423A]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          >
            {t(
              "hero_intro",
              "Art inspired by God, created to strengthen faith and bring the Word to life through creativity. Each piece is a visual illustration of scripture — painted to equip, encourage, and endure."
            )}
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col gap-4 sm:flex-row"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          >
            <Link to="/portfolio" className="btn-primary" data-testid="hero-explore-button">
              Explore the Art <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/booking" className="btn-outline" data-testid="hero-book-button">
              Book a Consultation
            </Link>
          </motion.div>
        </div>

        <motion.div
          style={{ y: artY }}
          className="relative lg:col-span-5"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
        >
          <div className="relative mx-auto max-w-sm lg:max-w-none">
            <motion.div style={{ x: bigX, y: bigY }} className="relative">
              <div className="absolute -inset-3 rounded-t-[10rem] border border-[#D4AF37]/40" />
              <div className="relative aspect-[3/4] overflow-hidden rounded-t-[10rem] border border-[#D4AF37]/50 shadow-2xl">
                <img
                  src="/assets/anointed-pour-1.webp"
                  alt="The Anointed Pour — original artwork by Natalia"
                  className="h-full w-full object-cover"
                />
              </div>
            </motion.div>
            <motion.div
              style={{ x: smallX, y: smallY }}
              className="absolute -bottom-10 -left-6 w-32 sm:w-40"
            >
              <div className="aspect-[4/5] overflow-hidden rounded-sm border border-[#D4AF37]/50 shadow-xl">
                <img
                  src="/assets/anointed-pour-2.webp"
                  alt="The Anointed Pour — full view of the original canvas"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
            <div className="absolute -right-3 top-8 hidden rounded-full border border-[#D4AF37]/40 bg-[#FAF7F2]/90 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#7A6A52] backdrop-blur-sm sm:block">
              Featured Work · The Anointed Pour
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="relative flex flex-col items-center gap-3 pb-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C7E70]">
          Scroll to Experience
        </span>
        <div className="h-12 w-px overflow-hidden bg-[#D4AF37]/25">
          <motion.div
            className="h-full w-full origin-top bg-[#B89759]"
            animate={{ scaleY: [0, 1, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}

function AboutPreview() {
  return (
    <section className="py-20 md:py-32" data-testid="home-about-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <div className="relative mx-auto max-w-sm">
            <div className="absolute -inset-3 rounded-t-[10rem] border border-[#D4AF37]/30" />
            <div className="relative aspect-[3/4] overflow-hidden rounded-t-[10rem] border border-[#D4AF37]/40 bg-[#F4EFE6]">
              <img
                src="/assets/natalya-portrait.webp"
                alt="Natalya — artist and founder of Diamond's Divine Arts"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <SectionHead
            kicker="About the Artist"
            title="The Hands Behind the Work"
            copy="Natalia creates at the intersection of faith and fine art. What began as a personal practice of painting through scripture has become Diamond's Divine Arts — a studio devoted to illustrating the Word and encouraging the saints through visual storytelling."
          />
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-[#4A423A]">
              Every canvas begins in prayer and ends with a purpose: that whoever encounters it
              would be strengthened in their faith.
            </p>
            <Link to="/about" className="btn-outline mt-9" data-testid="home-meet-artist-button">
              Meet the Artist <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function MissionPreview() {
  const { t } = useSiteData();
  return (
    <section className="border-y border-[#D4AF37]/20 bg-[#F4EFE6]/60 py-20 md:py-32" data-testid="home-mission-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          center
          kicker="The Mission"
          title="Experience the Word Through Creativity."
          copy={t(
            "mission_statement",
            "Diamond's Divine Arts exists to visually illustrate biblical themes — equipping people with the Word of God and strengthening faith through the lens of creativity."
          )}
        />
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {MISSION_CHAPTERS.slice(0, 3).map((c, i) => (
            <Reveal key={c.number} delay={i * 0.12}>
              <div className="border-t border-[#D4AF37]/40 pt-6">
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#B89759]">
                  Chapter {c.number}
                </span>
                <h3 className="mt-3 font-serif text-xl text-[#2A241F] sm:text-2xl">{c.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#4A423A]">{c.content}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 text-center">
          <Link to="/mission" className="btn-outline" data-testid="home-mission-button">
            Read the Full Mission <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function FeaturedWorks() {
  const navigate = useNavigate();
  const { artworks } = useSiteData();
  const featured = (artworks.filter((a) => a.featured).length ? artworks.filter((a) => a.featured) : artworks).slice(0, 3);
  return (
    <section className="py-20 md:py-32" data-testid="home-featured-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHead
            kicker="The Gallery"
            title="Featured Work"
            copy="A glimpse into the collection — original works created prayerfully in the studio."
          />
          <Reveal delay={0.1}>
            <Link to="/portfolio" className="btn-primary" data-testid="home-portfolio-button">
              View Full Portfolio <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((art, i) => (
            <ArtworkCard
              key={art.id}
              art={art}
              index={i}
              onView={(a) => navigate(`/portfolio?piece=${a.id}`)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="border-y border-[#D4AF37]/20 bg-[#171310] py-20 md:py-32" data-testid="home-services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          dark
          kicker="What the Studio Offers"
          title="Art, Made Personal"
          copy="From gallery originals to bespoke commissions and private consultations — every offering is designed around your story and your space."
        />
        <div className="mt-16 grid gap-px overflow-hidden rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/20 md:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal key={s.number} delay={i * 0.08} className="bg-[#171310]">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-[#1F1A15] sm:p-10">
                <span className="font-mono text-xs tracking-[0.3em] text-[#B89759]">{s.number}</span>
                <h3 className="mt-4 font-serif text-2xl text-[#FAF7F2]">{s.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#B7AA99]">{s.short}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12">
          <Link to="/services" className="btn-gold" data-testid="home-services-button">
            Explore Services <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function InstagramSection() {
  return (
    <section className="border-t border-[#D4AF37]/20 bg-[#F4EFE6]/60 py-20 md:py-32" data-testid="home-instagram-section">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <SectionHead
          center
          kicker="Follow the Journey"
          title="Follow Diamond's Divine Arts"
          copy="Studio moments, works in progress, and finished pieces — follow along on Instagram."
        />
        <Reveal delay={0.1} className="mt-10 flex justify-center">
          <a
            href={BUSINESS.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-testid="instagram-follow-button"
          >
            <Instagram className="h-4 w-4" /> Follow {BUSINESS.instagram}
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta(
    "Diamond's Divine Arts | Faith-Inspired Christian Art",
    "Discover Diamond's Divine Arts — faith-inspired artwork created to bring biblical stories, creativity, and inspiration to life. Explore the portfolio, connect with the artist, and book a consultation."
  );
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <AboutPreview />
      <MissionPreview />
      <FeaturedWorks />
      <ServicesPreview />
      <InstagramSection />
      <CtaBand />
    </>
  );
}
